import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import CommunityLayout from '../../../components/community/CommunityLayout';
import LoginModal from '../../../components/modals/LoginModal';
import { getPostList, PostListItem, ApiResponse, PostListResponse } from '../../../services/postApi';
import { searchNoticePosts } from '../../../services/searchApi';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import type { PostSearchItem } from '../../../services/searchApi';

const Notice: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [postList, setPostList] = useState<PostListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  
  // Redux 상태에서 로그인 정보 가져오기
  const { token } = useSelector((state: RootState) => state.auth);
  
  // 로그인 모달 상태
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 로그인 후 이동할 게시글 ID 저장
  const [pendingPostId, setPendingPostId] = useState<number | null>(null);
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<PostSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // 게시글 목록 조회
  const fetchPosts = useCallback(async (pageNum: number = 0, reset: boolean = false) => {
    /// console.log('공지사항 목록 API 호출 데이터:', { pageNum, reset });
    
    setLoading(true);
    try {
      const response: ApiResponse<PostListResponse> = await getPostList(
        'notice',
        pageNum,
        10
      );
      /// console.log('공지사항 목록 API 응답:', {response});
      /// console.log('API 응답 데이터:', response.data);
      /// console.log('API 응답 데이터 타입:', typeof response.data);
      
      if (response.data) {
        const newPosts = response.data.content;
        /// console.log('받은 게시글 수:', newPosts.length);
        /// console.log('받은 게시글 데이터:', newPosts);
        /// console.log('현재 페이지:', pageNum, '리셋 여부:', reset);
        
        setPostList(reset ? newPosts : prev => [...prev, ...newPosts]);
        setHasMorePosts(!response.data.last);
        
        /// console.log('설정된 hasMorePosts:', !response.data.last);
      } else {
        /// console.log('API 응답에 data가 없습니다:', response);
      }
    } catch (error: any) {
      /// console.error('게시글 목록 조회 실패:', error);
      if (error.message === 'Unauthorized') {
        navigate('/login');
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    /// console.log('useEffect 실행:', { isSearchMode, fetchPosts });
    if (!isSearchMode) {
      /// console.log('초기 데이터 로드 시작');
      fetchPosts(0, true);
    }
  }, [fetchPosts, isSearchMode]);

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams]);

  // 검색 실행 함수
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await searchNoticePosts(keyword);
      setSearchResults(results);
    } catch (error) {
      /// console.error('공지사항 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 더 많은 데이터 로드
  const loadMorePosts = useCallback(() => {
    if (hasMorePosts && !loading && !isSearchMode) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage, false);
    }
  }, [hasMorePosts, loading, isSearchMode, page, fetchPosts]);

  // 무한스크롤 적용
  useInfiniteScroll({
    hasMore: hasMorePosts && !isSearchMode,
    isLoading: loading,
    onLoadMore: loadMorePosts,
    threshold: 200
  });

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    // Redux와 localStorage 둘 다 확인
    const reduxToken = token;
    const localToken = localStorage.getItem('access_token');
    const finalToken = reduxToken || localToken;
    
    // 토큰이 없으면 로그인 모달 띄우기
    if (!finalToken) {
      setPendingPostId(postId); // 로그인 후 이동할 게시글 ID 저장
      setShowLoginModal(true);
      return;
    }
    
    // 토큰이 있으면 게시글 페이지로 이동
    navigate(`/community/post/${postId}`);
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    
    // 로그인 후 대기 중인 게시글로 자동 이동
    if (pendingPostId) {
      // /// console.log(`로그인 완료. 게시글 ${pendingPostId}로 이동합니다.`);
      navigate(`/community/post/${pendingPostId}`);
      setPendingPostId(null); // 상태 초기화
    }
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
    setPendingPostId(null); // 모달을 닫을 때도 상태 초기화
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/community/notice?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/community/notice');
      }
    }
  };

  return (
    <CommunityLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>공지사항</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커뮤니티</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>공지사항</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력해주세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* 공지사항 목록 */}
          <NoticesList>
            {isSearchMode ? (
              // 검색 모드
              <>
                {searchLoading ? (
                  <LoadingState>검색 중...</LoadingState>
                ) : searchError ? (
                  <EmptyState>{searchError}</EmptyState>
                ) : searchResults.length === 0 ? (
                  <EmptyState>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyState>
                ) : (
                  searchResults.filter(notice => notice !== null && notice !== undefined).map((notice, index) => (
                    <React.Fragment key={notice?.id || index}>
                      <NoticeCard onClick={() => notice?.id && handlePostClick(notice.id)}>
                        <NoticeContent>
                          <NoticeHeader>
                            <NoticeTitle>{notice?.title || '제목 없음'}</NoticeTitle>
                          </NoticeHeader>
                          
                          <NoticeFooter>
                            <NoticeAuthorInfo>
                              <AuthorAvatar>
                                <img src={notice?.writerProfileImageUrl || "/images/default-avatar.png"} alt="작성자" />
                              </AuthorAvatar>
                              <AuthorName>{notice?.writerName || '알 수 없음'}</AuthorName>
                              <Divider />
                              <NoticeDate>{notice?.postDate ? new Date(notice.postDate).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              }) : '날짜 없음'}</NoticeDate>
                            </NoticeAuthorInfo>
                          </NoticeFooter>
                        </NoticeContent>
                      </NoticeCard>
                      
                      {index < searchResults.length - 1 && <NoticeDivider />}
                    </React.Fragment>
                  ))
                )}
              </>
            ) : (
              // 기본 모드
              <>
                {console.log('렌더링 상태:', { loading, postListLength: postList.length, isSearchMode })}
                {loading ? (
                  <LoadingState>
                    게시글을 불러오는 중...
                  </LoadingState>
                ) : postList.length === 0 ? (
                  <EmptyState>
                    작성된 공지사항이 없습니다.
                  </EmptyState>
                ) : (
                  postList.filter(notice => {
                    const isValid = notice !== null && notice !== undefined;
                    if (!isValid) {
                      /// console.log('필터링된 게시글:', notice);
                    }
                    return isValid;
                  }).map((notice, index) => {
                    return (
                    <React.Fragment key={notice?.post_id || index}>
                      <NoticeCard onClick={() => notice?.post_id && handlePostClick(notice.post_id)}>
                        <NoticeContent>
                          <NoticeHeader>
                            <NoticeTitle>{notice?.title || '제목 없음'}</NoticeTitle>
                          </NoticeHeader>
                          
                          <NoticeFooter>
                            <NoticeAuthorInfo>
                              <AuthorAvatar>
                                <img src={notice?.writer_profile_image_url || "/images/default-avatar.png"} alt="작성자" />
                              </AuthorAvatar>
                              <AuthorName>{notice?.writer_name || '알 수 없음'}</AuthorName>
                              <Divider />
                              <NoticeDate>{notice?.created_at ? new Date(notice.created_at).toLocaleString('ko-KR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              }) : '날짜 없음'}</NoticeDate>
                            </NoticeAuthorInfo>
                          </NoticeFooter>
                        </NoticeContent>
                      </NoticeCard>
                      
                      {index < postList.length - 1 && <NoticeDivider />}
                    </React.Fragment>
                    );
                  })
                )}
                
                {/* 무한스크롤 로딩 표시 */}
                {loading && postList.length > 0 && !isSearchMode && (
                  <LoadingState>추가 게시글을 불러오는 중...</LoadingState>
                )}
              </>
            )}
          </NoticesList>
        </MainContainer>
        
        {/* 로그인 모달 */}
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      </Container>
    </CommunityLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #171719;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const NoticesList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: -32px;
`;

const NoticeCard = styled.div`
  width: 100%;
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  cursor: pointer;
`;

const NoticeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const NoticeHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoticeTitle = styled.h3`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26.01px;
  margin: 0;
`;

const NoticeFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 32px;
`;

const NoticeAuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const NoticeDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const NoticeDivider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;


export default Notice;